import React, { useState, useEffect, useMemo } from "react";
import "./DayoffApproval.css";
import { getDayoffReports, updateDayoffStatus } from "../../../api/reportsApi";
import Preloader from "../../Preloader/Preloader";
import { usePendingReports } from "../../../contexts/PendingReportsContext";

function DayoffApproval() {
  const { updatePendingCounts } = usePendingReports();
  const [dayoffReports, setDayoffReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingStatuses, setSavingStatuses] = useState({});
  const [showPopup, setShowPopup] = useState(false);

  const fetchDayoffReports = async () => {
    try {
      const response = await getDayoffReports();
      console.log("Полученные отчеты:", response);

      if (!Array.isArray(response)) {
        console.error("Получены некорректные данные:", response);
        setError("Получены некорректные данные от сервера");
        setDayoffReports([]);
        return;
      }

      const formattedReports = response.map((report) => ({
        id: report.id,
        userName: `${report.surname} ${report.name} ${report.patronymic}`,
        date: new Date(report.date_of_use).toLocaleDateString("ru-RU"),
        hours: Math.floor(report.time / 3600), // конвертируем секунды в часы
        isApproved: null,
        group: report.groups[0], // берем первую группу из массива
        role: report.roles[0], // берем первую роль из массива
      }));
      setDayoffReports(formattedReports);
    } catch (err) {
      setError("Ошибка при загрузке заявок на отгулы");
      console.error("Подробности ошибки:", err);
      setDayoffReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDayoffReports();
  }, []);

  const groupedReports = useMemo(() => {
    const groups = {};
    dayoffReports.forEach((report) => {
      if (!groups[report.group]) {
        groups[report.group] = {};
      }
      if (!groups[report.group][report.role]) {
        groups[report.group][report.role] = {};
      }
      if (!groups[report.group][report.role][report.userName]) {
        groups[report.group][report.role][report.userName] = [];
      }
      groups[report.group][report.role][report.userName].push(report);
    });
    return groups;
  }, [dayoffReports]);

  const handleApprove = (reportId) => {
    setDayoffReports((reports) =>
      reports.map((report) =>
        report.id === reportId
          ? { ...report, isApproved: report.isApproved === true ? null : true }
          : report
      )
    );
  };

  const handleReject = (reportId) => {
    setDayoffReports((reports) =>
      reports.map((report) =>
        report.id === reportId
          ? {
              ...report,
              isApproved: report.isApproved === false ? null : false,
            }
          : report
      )
    );
  };

  const handleSave = async () => {
    const reportsToUpdate = dayoffReports.filter(
      (report) => report.isApproved !== null
    );

    if (reportsToUpdate.length === 0) {
      setError("Нет отгулов для сохранения");
      return;
    }

    try {
      for (const report of reportsToUpdate) {
        console.log("Отправляем отгул на сервер:", {
          id: report.id,
          hours: report.hours,
          isApproved: report.isApproved,
          userName: report.userName,
          date: report.date,
        });

        setSavingStatuses((prev) => ({ ...prev, [report.id]: "pending" }));
        await updateDayoffStatus(report.id, report.isApproved);
        setSavingStatuses((prev) => ({ ...prev, [report.id]: "success" }));

      }

      setShowPopup(true);
      setTimeout(async () => {
        await updatePendingCounts();
        setShowPopup(false);
        setIsLoading(true);
        fetchDayoffReports();
      }, 1000);
    } catch (error) {
      console.error("Ошибка при сохранении:", error);
      setError("Произошла ошибка при сохранении статусов");
    }
  };

  const handleReset = () => {
    setDayoffReports((reports) =>
      reports.map((report) => ({ ...report, isApproved: null }))
    );
    setSavingStatuses({});
    setError(null);
  };

  if (isLoading) return <Preloader />;

  const hasReports = Object.keys(groupedReports).length > 0;

  return (
    <div className="overtime-approval">
      <h2 className="overtime-approval__title">Согласование отгулов</h2>
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <div className="popup-icon"></div>
            <p className="popup-text">Статусы отгулов успешно сохранены</p>
          </div>
        </div>
      )}
      {!hasReports ? (
        <div className="overtime-approval__empty">
          Отгулы для согласования отсутствуют
        </div>
      ) : (
        <>
          {error && <div className="overtime-approval__error">{error}</div>}
          <div className="overtime-approval__content">
            {Object.entries(groupedReports).map(([group, roles]) => (
              <div key={group} className="overtime-approval__group">
                <h3 className="overtime-approval__group-title">{group}</h3>
                {Object.entries(roles).map(([role, users]) => (
                  <div key={role} className="overtime-approval__role">
                    <h4 className="overtime-approval__role-title">{role}</h4>
                    {Object.entries(users).map(([userName, reports]) => (
                      <div key={userName} className="overtime-approval__user">
                        <h5 className="overtime-approval__user-title">
                          {userName}
                        </h5>
                        <table className="overtime-approval__table">
                          <thead>
                            <tr>
                              <th>Дата</th>
                              <th>Часы</th>
                              <th>Подтверждение</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reports.map((report) => (
                              <tr key={report.id}>
                                <td>{report.date}</td>
                                <td>{report.hours}</td>
                                <td className="overtime-approval__actions">
                                  <div className="overtime-approval__status-wrapper">
                                    <button
                                      className={`overtime-approval__action-btn ${
                                        report.isApproved === true
                                          ? "active"
                                          : ""
                                      } ${
                                        savingStatuses[report.id] === "pending"
                                          ? "loading"
                                          : ""
                                      }`}
                                      onClick={() => handleApprove(report.id)}
                                      disabled={
                                        savingStatuses[report.id] === "pending"
                                      }
                                    >
                                      <span className="overtime-approval__check">
                                        ✓
                                      </span>
                                    </button>
                                    <button
                                      className={`overtime-approval__action-btn ${
                                        report.isApproved === false
                                          ? "active"
                                          : ""
                                      } ${
                                        savingStatuses[report.id] === "pending"
                                          ? "loading"
                                          : ""
                                      }`}
                                      onClick={() => handleReject(report.id)}
                                      disabled={
                                        savingStatuses[report.id] === "pending"
                                      }
                                    >
                                      <span className="overtime-approval__cross">
                                        ✕
                                      </span>
                                    </button>
                                    {savingStatuses[report.id] === "error" && (
                                      <span
                                        className="overtime-approval__error-icon"
                                        title="Ошибка сохранения"
                                      >
                                        ⚠️
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="overtime-approval__buttons">
            <button className="button button_type_primary" onClick={handleSave}>
              Сохранить
            </button>
            <button
              className="button button_type_secondary"
              onClick={handleReset}
            >
              Сбросить
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default DayoffApproval;
